import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DAY_MARKER, worklogFacetsSchema } from '@forecastify/shared';
import { useDuration } from '../../duration';
import { useSession } from '../../session';
import { useFilterOptions } from '../use-filter-options';
import {
  type MemberSkill,
  type Skill,
  type SkillLevel,
  BILLING_KINDS,
  CURRENCY_CATALOGUE,
  PROJECT_STATUSES,
  SENIORITY_LEVELS,
  billablePercent,
  formatMoney,
  formatSort,
  toWireDate,
  taskSchema,
  worklogSchema,
  type IdentityRow,
  type Metafield,
  type BillingKind,
  type Person,
  type TeamRef,
  type PersonSchedule,
  type RateAssignment,
  type Project,
  type Task,
  type TaskFacets,
  type Worklog,
  type WorklogFacets,
  filterItems,
} from '@forecastify/shared';
import {
  AvatarFace,
  Badge,
  Button,
  DataList,
  Icon,
  MultiSelect,
  Select,
  TextInput,
  Tooltip,
  type DataListRow,
  type IconName,
  type SortState,
} from '@forecastify/ui';
import { useI18n, useT, type Translate } from '../../i18n';
import { usePageHeader } from '../page-header';
import { DetailBox, DetailRow, DetailValue, INLINE_INPUT } from '../card';
import { MetafieldForm } from '../metafields/metafield-form';
import { Attachments } from '../attachments';
import { DOCUMENT_FILES } from '../file-kinds';
import { ScopedDropSurface } from '../scoped-drop';
import { ProviderLogo } from '../integrations/provider-logo';
import { zoneOptions } from '../timezones';
import { usePersonName } from '../person-name';
import { SkillRows } from '../account/skills-section';
import { ItemPicker } from '../item-picker';
import { employmentLabel, statusLabel } from './people-screen';
import { initialsOf } from '../identity';
import { DateField, useCalendarLabels } from '../date-field';
import { listLabels } from '../delivery/clients-screen';
import { statusKey } from '../delivery/project-dialog';
import { sortInMemory } from '../sort-in-memory';
import { useRowPages } from '../use-row-pages';
import { useServerList } from '../use-server-list';
import { taskColumns, taskRows, type TaskColumnKey } from '../tasks/task-columns';
import { CollapsibleHistory } from '../history/collapsible-history';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ One person — `main.jsx` 4880–4990, `markup.html` 1000–1260.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The header, four KPIs, the editable facts, four tabs, the metafields.
 *
 * ⚠️⚠️ **THE KPI SET CHANGES WITH `tracksTime`, AND THAT IS THE MOCKUP'S OWN RULE.** Somebody who is
 * not expected to log hours gets *"Időrögzítés — Nincs"* where the hours tile would be, because a
 * `0:00` there is not a fact about them. The Időlogok tab disappears for the same reason.
 *
 * ⚠️ **Every field saves ONE field**, which is what `PATCH /people/:id` is shaped for — the mockup's
 * `pxPatch(id, { team })` is the whole interaction model.
 */

export type PersonTab = 'projects' | 'tasks' | 'logs' | 'history';

export interface PersonScreenProps {
  readonly person: Person;
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly projects: readonly Project[];
  /**
   * 31 Oct 2026 · how many tasks are theirs and how many of those are still open, counted on the
   * server. `null` until it lands — a `0` would read as a fact about them.
   */
  readonly taskCounts?: { readonly total: number; readonly open: number } | null;
  /** 31 Oct 2026 · what their task section's menus offer — `GET /tasks/facets`. */
  readonly taskFacets?: TaskFacets | null;
  /** 31 Oct 2026 · what their time-log section's menus offer — `GET /worklogs/facets`. */
  readonly logFacets?: WorklogFacets | null;
  /** 31 Oct 2026 · the month the hours grid draws — their newest one with hours — and its hours. */
  readonly recentMonth?: { readonly month: string; readonly worklogs: readonly Worklog[] } | null;
  /** 31 Oct 2026 · moves on every reload, so the two server-paged sections re-read their page. */
  readonly nonce?: number;
  readonly metafields: readonly Metafield[];
  /**
   * C54.1 · ⚠️ **This person's skills, plus the vocabulary and levels the picker needs.**
   * Optional because every embedded copy of this screen that does not fetch them renders the
   * panel empty rather than failing — the same shape the metafields prop takes.
   */
  readonly skills?: readonly MemberSkill[];
  readonly skillLevels?: readonly SkillLevel[];
  readonly skillVocabulary?: readonly Skill[];
  readonly skillError?: string | null;
  readonly onSetSkill?: (skillId: string, levelId: string) => void;
  readonly onRemoveSkill?: (skillId: string) => void;
  /** Everybody who could be this person's manager — the live members, minus themselves. */
  readonly managers: readonly { id: string; name: string }[];
  /**
   * 17 Oct 2026 · the workspace's project roles, for *Alap szerep*. Optional: a copy of this
   * screen that does not fetch them shows the person's role read-only rather than an empty picker.
   */
  readonly projectRoles?: readonly { id: string; name: string; color: string }[];
  /**
   * C45 · what schedule they are on TODAY, or `null` if they have none.
   *
   * ⚠️ **Null is a real answer and the row says so.** Somebody hired next month has no schedule
   * yet, and C44 turns exactly that absence into *nincs napi terv* rather than a shortfall.
   */
  readonly schedule: PersonSchedule | null;
  /**
   * Turns leave management on or off for this person. Absent for a reader who may not manage
   * people — the row is then read-only rather than a control that fails.
   */
  readonly onLeaveManaged?: (managed: boolean) => void;
  /** The workspace's roles, for the *Jogosultság* picker. */
  readonly roleOptions?: readonly { readonly id: string; readonly name: string }[];
  /**
   * Gives this person the role. ⚠️ **Absent means read-only** — without `people.manage`, and on
   * your OWN page, which the server refuses (`cannot_change_own_roles`): a control that is always
   * refused is worse than a value.
   */
  readonly onRole?: (roleId: string) => void;
  /** Opens Beállítások → Munkarendek, where a schedule is actually changed. Absent for a reader
   *  who may not manage settings — a link to a screen that refuses is worse than none. */
  readonly onOpenSchedules?: () => void;
  /**
   * C51 · the díjkártya in force today, or `null`.
   *
   * ⚠️⚠️ **NULL ALSO MEANS *YOU MAY NOT SEE IT*, AND THAT IS WHY THE CARD IS GATED SEPARATELY.**
   * The read behind it needs `settings.view`, so a reader without that always gets null — and a
   * card saying *nincs díjkártya* to somebody who simply was not allowed to look would be a lie
   * about the person. `canSeeRates` decides whether the card exists at all; `rate` only decides
   * what it says. The mockup gates its own with `persRates: seeRates && !limited`.
   */
  readonly rate: RateAssignment | null;
  /** Whether the DÍJ card is drawn at all — `settings.view`. See `rate`. */
  readonly canSeeRates: boolean;
  /** Opens Beállítások → Díjkártyák → Hozzárendelések, where a rate is actually changed. */
  readonly onOpenRates?: () => void;
  /** R-60 · the workspace's teams, for the multi-select. */
  readonly teams: readonly TeamRef[];
  readonly canManage: boolean;
  /**
   * C39 cleanup · the workspace's tracker accounts, so this person's own can be set here.
   *
   * ⚠️ **The whole workspace's, not this person's**, because the control is a picker: what it
   * offers is *the accounts nobody has claimed*, plus whichever one is already theirs.
   */
  readonly identities: readonly IdentityRow[];
  readonly canPairIdentity: boolean;
  readonly onPairIdentity: (identityId: string, memberId: string | null) => void;
  /** `people.onboard`. See `people-screen.tsx` for why it is not `people.manage`. */
  readonly canOnboard: boolean;
  readonly serverError: string | null;
  readonly onBack: () => void;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ WHICH FRAME THIS IS DRAWN IN — item 46.                          │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * *"Person gains modal and drawer — fullscreen is the only view today."* A person was reachable
   * only as a route, so every look at one cost the list: its page, its filters and its scroll
   * position, all re-fetched on the way back.
   *
   * ⚠️⚠️ **ONLY `full` CLAIMS THE SHELL'S HEADER.** `usePageHeader` writes into the chrome above
   * this screen, which a modal is drawn OVER — so a person opened in one would have rewritten the
   * people list's own header behind the scrim, and closing it would have left the wrong name up
   * there. The surface draws its own strip instead.
   *
   * ⚠️ **The layout follows the frame, not the window**: a 560px drawer gets the narrow single
   * column, a modal and a page get the two-column grid. `wide` is that.
   */
  readonly mode?: 'full' | 'modal' | 'drawer';
  readonly onPatch: (patch: Record<string, unknown>) => void;
  /**
   * ⚠️ `0116` · a document about this person. Absent for a reader who may not manage people, which
   * also hides the section — a folder somebody can neither fill nor read is not a folder.
   */
  readonly onAttach?: (file: File) => Promise<void>;
  readonly onOpenTask: (task: Task) => void;
  readonly onOnboard: () => void;
  /** C14 status. Its own endpoint, not a field patch — see the box that calls it. */
  readonly onStatus: (next: 'active' | 'deactivated') => void;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C39 cleanup · which tracker account is this person. On their own page.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS MOVED OFF THE PEOPLE LIST, AND THE MOVE FIXES WHAT THE PANEL WAS BAD AT.** It used to
 * be a table of every unpaired Jira account on the list screen — a list of strangers, each needing
 * to be matched against a name, by somebody who had come to that screen to do something else. The
 * question is *"who is this person in Jira?"*, and the place it can be answered is the page about
 * that person.
 *
 * ⚠️⚠️ **ABSENT UNLESS THERE IS SOMETHING TO SET.** A workspace with no tracker, or one where every
 * account is already claimed by somebody else, gets no box at all — a control that can only ever
 * say *nothing to choose* teaches people to stop reading the rail.
 *
 * ⚠️⚠️ **AND AN OAUTH PAIRING IS SHOWN WITHOUT A CONTROL** (`0085`). That one is not an
 * administrator's guess: the account holder consented and Atlassian named them. The server refuses
 * to change it, so offering a select here would be offering an action that fails.
 */
function IntegrationBox({
  t,
  person,
  identities,
  canManage,
  onPair,
}: {
  readonly t: Translate;
  readonly person: Person;
  readonly identities: readonly IdentityRow[];
  readonly canManage: boolean;
  readonly onPair: (identityId: string, memberId: string | null) => void;
}) {
  /**
   * ⚠️ **Grouped by SITE, not by instance.** An account belongs to a Jira site; two instances can
   * read the same one, which is why `instanceNames` is a list. Grouping by instance would offer the
   * same person twice and let somebody pair them inconsistently.
   */
  const sites = new Map<
    string,
    { label: string; provider: string | null; mine: IdentityRow | null; free: IdentityRow[] }
  >();
  for (const row of identities) {
    const site = sites.get(row.remoteSiteId) ?? {
      label: row.instanceNames.join(', '),
      /* ⚠️ One provider per SITE — every identity on it reports the same one. */
      provider: row.provider,
      mine: null,
      free: [],
    };
    if (row.memberId === person.id) site.mine = row;
    else if (row.memberId === null) site.free.push(row);
    sites.set(row.remoteSiteId, site);
  }

  /* ⚠️ A site with nothing of theirs and nothing free has no question to ask. */
  const rows = [...sites.values()].filter((site) => site.mine !== null || site.free.length > 0);
  if (rows.length === 0) return null;

  return (
    <section className="bg-card rounded-xl border p-3 shadow-sm">
      <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {t('people.integration.title')}
      </div>

      {rows.map((site, index) => {
        const proven = site.mine?.matchedBy === 'oauth';
        return (
          <div key={site.label + String(index)} className="mt-1.5">
            {/* ⚠️⚠️ **THE INSTANCE NAME AND THE MARK, ALWAYS — AND THEY USED TO APPEAR FOR
                NEITHER OF THE TWO COMMON CASES.** The label was drawn only for a workspace with
                more than one site, on the reasoning that one site makes it a label repeating
                itself; and the logo was drawn only inside the *proven* box. A workspace with one
                Jira and a pairing made by hand — which is most of them - therefore got a box
                headed INTEGRATION SETTINGS with a dropdown in it and nothing saying which tracker
                or which instance it was about. Reported exactly that way.
                ⚠️ `ProviderLogo` draws nothing for a site no instance points at any more, which
                is a real state and the reason the row is not conditioned on the provider. */}
            <div className="text-muted-foreground mb-1 flex items-center gap-1.5 text-[11.5px]">
              <ProviderLogo provider={site.provider} size={13} />
              {site.label}
            </div>

            {proven ? (
              <>
                <div className="bg-muted flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12px]">
                  {/* ⚠️ The mark moved to the header above, which now draws unconditionally; two
                      copies of it in a box this small read as a mistake. */}
                  <Icon name="user-round" size={13} className="text-muted-foreground" />
                  {site.mine?.remoteName}
                </div>
                {/* ⚠️ Says WHY it cannot be changed. *"Read-only"* with no reason reads as a bug. */}
                <p className="text-muted-foreground mt-1.5 text-[11.5px] leading-relaxed">
                  {t('people.integration.proven')}
                </p>
              </>
            ) : (
              <Select
                /* ⚠️ `inline`, like every other control in a detail rail — item 48's *"every
                   detail input field ever on any page"*. The box appears on hover and on focus. */
                inline
                className="w-full"
                /* ⚠️ Searchable (13 Oct 2026): a site's account list is as long as its user
                   directory, and scrolling a few hundred names for one is the complaint. */
                searchable
                searchPlaceholder={t('people.integration.search')}
                value={site.mine?.id ?? ''}
                disabled={!canManage}
                aria-label={t('people.integration.title')}
                onChange={(event) => {
                  const next = event.target.value;
                  /* ⚠️ Unpairing the old one first: a member with two accounts on one site is a
                     state nothing else in the system expects, and the pair endpoint is one verb. */
                  if (site.mine !== null && site.mine.id !== next) onPair(site.mine.id, null);
                  if (next !== '') onPair(next, person.id);
                }}
                options={[
                  { value: '', label: t('people.integration.none') },
                  ...(site.mine === null
                    ? []
                    : [{ value: site.mine.id, label: site.mine.remoteName }]),
                  /* ⚠️ Only accounts nobody has claimed — offering a colleague's would be offering
                     to move their hours onto this person.
                     ⚠️⚠️ **THE NAME ALONE SINCE ITEM 48 — it was `name · email` and only for the
                     rows that HAD an email.** So the list read as a mixture: half the options were
                     a person's name and half were a name and an address, which is what *"a mix of
                     username and email"* names. Every row has a name; not every row has an
                     address, and the one that is always there is the one to show. */
                  ...site.free.map((row) => ({ value: row.id, label: row.remoteName })),
                ]}
              />
            )}
          </div>
        );
      })}

      <p className="text-muted-foreground mt-2 text-[11.5px] leading-relaxed">
        {t('people.integration.hint')}
      </p>
    </section>
  );
}

export function PersonScreen(props: PersonScreenProps) {
  const t = useT();
  const { fmt, fmtNum, unit } = useDuration();
  const { locale } = useI18n();
  const { person, canManage, canOnboard, serverError } = props;
  /* ⚠️ Item 6 · the reader's order, out of the two halves — `person-name.ts` argues it, and the
     fallback to `displayName` is what an unsplit name (and an invitation) renders. */
  const name = usePersonName()(person);

  const readOnly = !canManage || person.deletedAt !== null;

  /* ── the KPIs ─────────────────────────────────────────────────────────────────────────── */

  const loading = props.loading === true;
  /* ⚠️ No figure until the counts land — a `0` here would read as a fact about them. Counted on
     the server (31 Oct 2026): the page no longer holds every task to count them. */
  const taskCounts = loading ? null : (props.taskCounts ?? null);
  const openTasksTile = {
    label: t('people.kpi.openTasks'),
    value: taskCounts === null ? '–' : String(taskCounts.open),
    hint: taskCounts === null ? '' : t('people.kpi.openTasksHint', { total: taskCounts.total }),
  };

  /**
   * C63 · ⚠️⚠️ **THE SECOND TILE IS *SZÁMLÁZHATÓ*, AND IT COSTS THE ROLE TILE ITS PLACE.** That is
   * the mockup's own pair of KPI rows: somebody who tracks time gets the billable share, somebody
   * who does not gets their workspace role, because a share of no hours is not a figure. The role
   * is on the rail either way — this strip is what the month was.
   */
  const billable = billablePercent(person.monthSplit);
  const splitHours = Object.values(person.monthSplit).reduce(
    (sum, hours) => sum + Number(hours),
    0,
  );

  const kpis = person.tracksTime
    ? [
        {
          label: t('people.kpi.monthHours'),
          value: fmt(person.monthHours),
          hint: t('people.kpi.monthHoursHint'),
        },
        {
          label: t('people.kpi.billable'),
          /* ⚠️ An em-dash rather than `0%` for a month nobody has worked yet — the share does not
             exist, and a confident zero reads as a bad month rather than an empty one. */
          value: billable === null ? '–' : `${String(billable)}%`,
          hint:
            billable === null
              ? t('people.kpi.billableNone')
              : t('people.kpi.billableHint', {
                  hours: fmt(
                    String(
                      Number(person.monthSplit.fixed ?? 0) +
                        Number(person.monthSplit.time_and_material ?? 0),
                    ),
                  ),
                }),
        },
        openTasksTile,
        {
          label: t('people.kpi.projects'),
          value: String(person.projectCount),
          hint: t('people.kpi.projectsHint'),
        },
      ]
    : [
        {
          /* ⚠️ The mockup's own substitution: the hours tile becomes a statement about why there
           * are none. */
          label: t('people.column.tracks'),
          value: t('people.tracks.no'),
          hint: t('people.kpi.noTracksHint'),
        },
        openTasksTile,
        {
          label: t('people.column.status'),
          value: statusLabel(t, person.status),
          hint: person.deletedAt === null ? '' : t('people.status.deleted'),
        },
        {
          label: t('people.column.roles'),
          /* 22 Oct 2026 · a role somebody created has no code (`0009`) — its name is the value. */
          value:
            person.roles[0] === undefined
              ? '–'
              : person.roles[0].code === ''
                ? person.roles[0].name
                : person.roles[0].code,
          hint:
            person.roles[0] === undefined
              ? t('people.kpi.noRole')
              : person.roles[0].code === ''
                ? ''
                : person.roles[0].name,
        },
      ];

  /* ── the tabs ─────────────────────────────────────────────────────────────────────────── */

  /**
   * ⚠️⚠️ **THE PERSON'S IDENTITY IS THE SHELL'S HEADER, not a card under it.** `markup.html`
   * 197–203 is the contract: back, the name at `text-[13.5px] font-medium`, the email muted and
   * mono at 12px. This screen drew a 60px bar of its own beneath the chrome's, so it opened with
   * *Dolgozók* above the person's name.
   *
   * ⚠️ **The avatar goes with it.** It was a 32px circle in a 48px bar; the mockup draws none here,
   * and the initials say nothing the name beside them does not.
   *
   * ⚠️ **The onboarding button stays on the RIGHT**, which is where every other screen's primary
   * action sits — and absent on a tombstone, because P-2 froze the row.
   */
  /* ⚠️ `0116` · an upload in flight, and a key that makes the list re-read once it lands. */
  const [attachNonce, setAttachNonce] = useState(0);
  /** The skill picker under *Skillek*'s add link. */
  const [pickingSkill, setPickingSkill] = useState(false);

  const mode = props.mode ?? 'full';
  const wide = mode !== 'drawer';

  /**
   * ⚠️⚠️ **`null` UNLESS THIS IS THE FULL PAGE, AND THE HOOK IS STILL CALLED.** React refuses a
   * conditional hook, and `usePageHeader` returning early inside itself would be a second rule to
   * remember; passing `null` is the documented way to claim nothing. A modal that claimed the
   * chrome would rewrite the list's own header behind its scrim.
   */
  usePageHeader(
    () =>
      mode !== 'full' ? null : (
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            className="-ml-1.5"
            onClick={props.onBack}
            aria-label={t('people.action.back')}
          >
            <Icon name="chevron-left" size={16} />
          </Button>

          {/**
           * ┌───────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ THE IDENTITY ROW ITSELF, NOT A SECOND COPY OF THE NAME.
           * └───────────────────────────────────────────────────────────────┘
           *
           * The page drew the name twice: once here in the shell's header bar, and again 40px
           * below it as an avatar-and-chips strip at 19px. Reported as *"why do we still have a
           * double header"* - and the answer is that item 48 built the strip without taking the
           * plain title away, so the two rows said the same thing at two sizes.
           *
           * ⚠️ **The strip moved UP rather than the title moving down.** The header bar is
           * where every screen's identity and primary action live; the avatar, the status and the
           * copyable chips belong with them, and the body then starts on the KPIs, which is what
           * the tab is for.
           */}
          <PersonIdentity t={t} person={person} name={name} />

          <div className="flex-1" />

          {canOnboard && person.deletedAt === null ? (
            <Button variant="outline" size="sm" onClick={props.onOnboard}>
              <Icon name="sparkles" size={14} />
              {person.onboardedAt === null
                ? t('people.action.onboardStart')
                : t('people.action.onboardReopen')}
            </Button>
          ) : null}
        </>
      ),
    /* ⚠️ VALUES ONLY — every callback here is a fresh closure each render. See `usePageHeader`. */
    [
      name,
      person.email,
      person.phone,
      person.status,
      person.deletedAt,
      person.onboardedAt,
      canOnboard,
      mode,
    ],
  );

  return (
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE WHOLE PERSON TAKES A DROP — item 9, second pass, 15 Sep.         │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * The attachments box shipped with a `DropZone` inside it: a 200px dashed rectangle you have to
     * find, scroll to and hit. Task, contract and line item all got the Jira gesture on 14 Sep and
     * this one did not, so the same act worked differently on the fourth surface — which is the
     * failure item 5 exists to prevent.
     *
     * ⚠️ **There are no zones here.** A person has no description and no comment box, so every drop
     * lands in the person's folder and the veil frames the whole surface. The zone machinery costs
     * nothing when nothing opts in.
     *
     * ⚠️ **The small `DropZone` stays inside the box**, which is not redundancy: it is the only
     * affordance a reader sees before they start dragging, and it carries the accepted types and
     * the size ceiling in words.
     *
     * ⚠️ **`data-screen` only on the full page.** The route-render gate reads it to prove an
     * address drew its OWN screen; a modal over the people list is not that address, and marking it
     * would let `/people` pass the check by drawing somebody's detail.
     */
    <ScopedDropSurface
      entityLabel={name}
      enabled={props.canManage && props.onAttach !== undefined}
      zoneLabels={{}}
      onFiles={(files) => {
        const file = files[0];
        if (file === undefined) return;
        void (async () => {
          /* ⚠️ No `busy` flag: `Attachments` disables its own control while an upload runs, and
             a page-wide drop has no control of its own to disable. */
          await props.onAttach?.(file);
          setAttachNonce((count) => count + 1);
        })();
      }}
      className="flex min-h-0 flex-1 flex-col"
    >
      <div
        {...(mode === 'full' ? { 'data-screen': 'person' } : {})}
        className="flex min-h-0 flex-1 flex-col"
      >
        {serverError !== null && (
          <p
            role="alert"
            className="border-destructive/40 bg-destructive/10 text-destructive mx-5 mt-3 rounded-md border px-3 py-2 text-[12.5px]"
          >
            {serverError}
          </p>
        )}

        {/* ⚠️ Says the row is frozen, because every control below is inert and nothing else would
          explain why. */}
        {person.deletedAt !== null && (
          <p className="text-muted-foreground mx-5 mt-3 rounded-md border px-3 py-2 text-[12.5px]">
            {t('people.deletedNote')}
          </p>
        )}

        {/**
         * ⚠️⚠️ **THE MOCKUP STACKS EVERYTHING; IT DOES NOT TAB.** `people.markup.html` 75–296 draws
         * Időlogok, Projektek, Feladatok and Aktivitás one under another in a single body column,
         * beside a narrow rail of Állapot / Részletek / Metafieldek. Ours put all four behind a tab
         * strip, so three quarters of what the page is for was one click away and invisible on
         * arrival — and the person's own hours, which is the reason most people open this page, were
         * not the thing it opened on.
         *
         * ⚠️ **The grid is the mockup's**: `lg:grid-cols-[minmax(360px,1fr)_308px]`, the header
         * spanning both columns, the rail on the right and the body below it on the left.
         */}
        {/**
         * ⚠️⚠️ **THE RAIL SCROLLS ON ITS OWN — 14 Oct.** *"Scrolling the main part leaves the sidebar
         * intact"* is the product owner's sentence. In the wide frame on a wide window the page is two
         * scroll regions side by side, so the facts on the right stay where they are while the logs,
         * projects and tasks on the left run past them — `DetailFrame` in `detail-chrome.tsx` is the
         * same shape for the contract details.
         *
         * ⚠️ **The four figures moved INTO the body column** to make that possible. They spanned both
         * columns above the rail, which would have left them either scrolling away with nothing or
         * pinned and eating a fifth of a laptop screen; at the top of the body they scroll with the
         * content they summarise. Below `lg` it is one region again and the order is rail, then body.
         *
         * ⚠️ **`contents` in the drawer**, so the body wrapper vanishes and `order-*` puts the
         * figures, the rail and the body back in the single column the drawer has always had.
         */}
        <div
          className={
            wide
              ? 'flex min-h-0 flex-1 flex-col overflow-y-auto lg:overflow-hidden'
              : 'flex-1 overflow-y-auto'
          }
        >
          {/* ⚠️ **The frame decides the columns, not the window** — item 46. `lg:` is a viewport
            query, so a 560px drawer on a wide screen would have taken the two-column grid and
            squeezed a 308px rail into it beside a 360px minimum that does not fit. */}
          <div
            className={
              wide
                ? 'flex flex-col gap-5 p-5 lg:min-h-0 lg:flex-1 lg:flex-row lg:gap-0 lg:p-0'
                : 'flex flex-col gap-5 p-4'
            }
          >
            <div
              className={
                wide
                  ? 'order-2 flex min-w-0 flex-col gap-5 lg:order-1 lg:min-w-[360px] lg:flex-1 lg:overflow-y-auto lg:py-5 lg:pr-3 lg:pl-5'
                  : 'contents'
              }
            >
              {/* ── the contact strip, the name, and the four figures ─────────────────────────── */}
              {/* ⚠️ The grid placements apply only in the wide frame — in a drawer the parent is a
              flex column and `col-start` names a track that does not exist. */}
              <div className={`flex min-w-0 flex-col gap-4 ${wide ? '' : 'order-1'}`}>
                {/**
                 * ⚠️ **Every chip COPIES rather than navigating** — `persCopyMail`, `persCopyPhone`.
                 * The address and the number are things people take away from this page, and a
                 * `mailto:` opens a client somebody may not use.
                 *
                 * ⚠️ **The mockup's `EMP-004` code chip is ABSENT and flagged**: `personSchema` has no
                 * employee code, so there is nothing to render. Inventing one would put an identifier
                 * on screen that no other surface knows.
                 */}
                {/**
                 * ┌──────────────────────────────────────────────────────────────┐
                 * │ ⚠️⚠️ ONE HEADER — item 48. Avatar, name, status, then the     │
                 * │ two copyable chips.                                          │
                 * └──────────────────────────────────────────────────────────────┘
                 *
                 * These were two stacked blocks: a row of contact chips, and below it the avatar,
                 * the name and the status badge. So the page opened on an email address and put the
                 * person's NAME second — and on a narrow surface the chips wrapped and pushed it
                 * further down.
                 *
                 * ⚠️ **Every chip COPIES rather than navigating** — `persCopyMail`, `persCopyPhone`.
                 * The address and the number are things people take away from this page, and a
                 * `mailto:` opens a client somebody may not use.
                 *
                 * ⚠️ **The mockup's `EMP-004` code chip is ABSENT and flagged**: `personSchema` has no
                 * employee code, so there is nothing to render. Inventing one would put an identifier
                 * on screen that no other surface knows.
                 *
                 * ⚠️ **The onboarding button is NOT here.** It lives in the shell's header, where
                 * every screen's primary action sits — putting it in the strip as well gave the page
                 * two identically-named buttons, which `getAllByRole` caught and a screen reader
                 * would have read out twice.
                 */}
                {/* ⚠️ **The identity strip that stood here is in the page header now** - see the
                memo at the top of this file. Removing it is half of *"the double header"*; the
                other half is that the header gained the avatar and the chips it used to hold. */}
                {/* ⚠️ The mockup's own KPI card: an 10.5px uppercase label, a 19px mono figure, a hint. */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {kpis.map((kpi) => (
                    <div key={kpi.label} className="bg-card rounded-xl border p-3 shadow-sm">
                      <div className="text-muted-foreground text-[10.5px] font-medium tracking-wide uppercase">
                        {kpi.label}
                      </div>
                      <div className="mt-1 text-[19px] tabular-nums">{kpi.value}</div>
                      {kpi.hint === '' ? null : (
                        <div className="text-muted-foreground mt-0.5 text-[11px]">{kpi.hint}</div>
                      )}
                    </div>
                  ))}
                </div>

                {/**
                 * ┌───────────────────────────────────────────────────────────────────────────┐
                 * │ C63 · *Elszámolás — e hónap* — drawn in the mockup since Phase 3.         │
                 * └───────────────────────────────────────────────────────────────────────────┘
                 *
                 * ⚠️⚠️ **IT NEEDED `0131` TO BE HONEST, WHICH IS WHY IT ARRIVES NOW.** The split is
                 * by the LINE ITEM's money type, and until a line item carried money there was
                 * nothing to group by — `PHASE-3.md` lists the card under that phase's live
                 * additions and it was never built.
                 *
                 * ⚠️ **Only where the reader may manage people, and only where the person tracks
                 * time** — the mockup's own `persShowSplit`. What somebody's hours are worth is a
                 * fact about their employment, not a public figure on a colleague's page.
                 *
                 * ⚠️ **Nothing logged yet draws NO bar at all.** An empty grey rail with a legend of
                 * zeroes is a chart claiming to show something; a sentence is the honest shape for
                 * the first week of a month.
                 */}
                {canManage && person.tracksTime && (
                  <div className="bg-card mt-3 rounded-xl border p-3 shadow-sm">
                    <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                      {t('people.split.title')}
                    </div>
                    {splitHours <= 0 ? (
                      <p className="text-muted-foreground mt-2 text-[11.5px]">
                        {t('people.split.empty')}
                      </p>
                    ) : (
                      <>
                        <div className="bg-muted mt-2 flex h-2 overflow-hidden rounded-full">
                          {BILLING_KINDS.filter(
                            (kind) => Number(person.monthSplit[kind] ?? 0) > 0,
                          ).map((kind) => (
                            <span
                              key={kind}
                              className="h-full"
                              style={{
                                width: `${String((Number(person.monthSplit[kind] ?? 0) / splitHours) * 100)}%`,
                                background: BILLING_COLOR[kind],
                              }}
                            />
                          ))}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                          {BILLING_KINDS.filter(
                            (kind) => Number(person.monthSplit[kind] ?? 0) > 0,
                          ).map((kind) => (
                            <span key={kind} className="flex items-center gap-1.5 text-[11.5px]">
                              <span
                                className="size-2 shrink-0 rounded-full"
                                style={{ background: BILLING_COLOR[kind] }}
                              />
                              {billingLabel(t, kind)}
                              <span className="tabular-nums">
                                {fmt(person.monthSplit[kind] ?? '0')}
                              </span>
                              <span className="text-muted-foreground tabular-nums">
                                {`${String(
                                  Math.round(
                                    (Number(person.monthSplit[kind] ?? 0) / splitHours) * 100,
                                  ),
                                )}%`}
                              </span>
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* ── the body: everything the tabs used to hide ────────────────────────────────── */}
              <div className={`flex min-w-0 flex-col gap-5 ${wide ? '' : 'order-3'}`}>
                {person.tracksTime ? (
                  <SectionBox title={t('people.tab.logs')}>
                    {/* ⚠️ The grid is their newest month, whatever the list below is filtered to. */}
                    <MonthOfHours
                      month={props.recentMonth?.month ?? null}
                      worklogs={props.recentMonth?.worklogs ?? []}
                    />

                    <PersonLogList
                      personId={person.id}
                      facets={props.logFacets ?? null}
                      nonce={props.nonce ?? 0}
                      loading={loading}
                    />
                  </SectionBox>
                ) : null}

                <SectionBox title={t('people.tab.projects')}>
                  <PersonProjectList projects={props.projects} loading={loading} />
                </SectionBox>

                {/**
                 * ┌───────────────────────────────────────────────────────────────────────────┐
                 * │ ⚠️⚠️ C54.1 · THE SKILLS BLOCK THE MOCKUP DRAWS AND C54 DID NOT BUILD.     │
                 * └───────────────────────────────────────────────────────────────────────────┘
                 *
                 * ⚠️⚠️ **IT WAS SKIPPED ON A WRONG READING OF THE MARKUP, AND THAT IS WORTH
                 * RECORDING.** C54 counted occurrences of `persSkillRows` in the Phase 5 standalone,
                 * found one, and concluded the accessor was drawn by nothing — one of the 140 dead
                 * keys. The count is too crude for that file: its component tree is built in
                 * JavaScript rather than in `sc-for` markup, so a key bound once in logic and
                 * rendered through a component reads the same as a key bound nowhere. The product
                 * owner's screenshot of the mockup settled it.
                 *
                 * ⚠️ **The same component as Fiókom → Skilljeim**, with this person's id instead of
                 * `me`. CLAUDE.md's frontend rule: a screen composes components, it does not define
                 * them — and two skill tables would drift on the first level somebody renamed.
                 *
                 * ⚠️ **`people.manage` decides whether it can be edited**, which is the permission
                 * story `skill.service.ts` already enforces: reading somebody else's skills needs
                 * `people.view`, changing them needs `people.manage`. The panel renders read-only
                 * without it rather than disappearing — what a colleague is good at is not secret.
                 */}
                {/**
                 * ⚠️⚠️ **A LIST IN THE SECTION'S OWN BOX, AND THE ADD IS A LINK ON ITS HEADING — 13 Oct
                 * 2026.** This drew Fiókom's whole skills CARD inside the box — a box in a box, with
                 * its own *Skilljeim* header speaking to the reader about a colleague. Now the box
                 * holds `SkillRows` alone, the add link sits beside *SKILLEK* the way *Csatolmány*
                 * sits on its box, and it opens `ItemPicker` — task linking's panel — over what is
                 * left to add. A pick lands at the middle level, which the row then edits in place.
                 */}
                <SectionBox
                  title={t('people.skills.title')}
                  actions={
                    !canManage || props.onSetSkill === undefined ? undefined : (
                      <span className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPickingSkill(true);
                          }}
                        >
                          <Icon name="plus" size={13} />
                          {t('people.skills.add')}
                        </Button>
                        {!pickingSkill ? null : (
                          <ItemPicker
                            title={t('people.skills.search')}
                            hint={t('account.skills.hint')}
                            items={(props.skillVocabulary ?? [])
                              .filter(
                                (skill) =>
                                  !(props.skills ?? []).some((row) => row.skillId === skill.id),
                              )
                              .map((skill) => ({
                                id: skill.id,
                                label: skill.name,
                                hint: skill.categoryName,
                                color: skill.categoryColor,
                              }))}
                            onPick={(skillId) => {
                              const levels = props.skillLevels ?? [];
                              const level = levels[Math.floor(levels.length / 2)] ?? levels[0];
                              setPickingSkill(false);
                              if (level !== undefined) props.onSetSkill?.(skillId, level.id);
                            }}
                            onClose={() => {
                              setPickingSkill(false);
                            }}
                          />
                        )}
                      </span>
                    )
                  }
                >
                  {props.skillError === undefined || props.skillError === null ? null : (
                    <p role="alert" className="text-destructive border-b px-3 py-2 text-[12px]">
                      {props.skillError}
                    </p>
                  )}
                  <SkillRows
                    skills={props.skills ?? []}
                    levels={props.skillLevels ?? []}
                    canEdit={canManage && props.onSetSkill !== undefined}
                    loading={props.loading === true}
                    emptyText={t('people.skills.empty')}
                    onSetLevel={(skillId, levelId) => {
                      props.onSetSkill?.(skillId, levelId);
                    }}
                    onRemove={(skillId) => {
                      props.onRemoveSkill?.(skillId);
                    }}
                  />
                </SectionBox>

                <SectionBox title={t('people.tab.tasks')}>
                  <PersonTaskList
                    personId={person.id}
                    facets={props.taskFacets ?? null}
                    nonce={props.nonce ?? 0}
                    loading={loading}
                    onOpen={props.onOpenTask}
                  />
                </SectionBox>

                {props.onAttach === undefined ? null : (
                  /* ⚠️ In the main column, above the activity feed (14 Oct 2026, ux-pass-2) — a
                   section like the contract's own, where documents have room to be read. */
                  <Attachments
                    key={attachNonce}
                    target="person"
                    targetId={person.id}
                    canEdit={props.canManage}
                    heading={t('person.attachments')}
                    variant="section"
                    emptyNote={t('person.attachments.empty')}
                    accept={DOCUMENT_FILES}
                    /* ⚠️ The box is drawn only when `onAttach` exists, so it does here. */
                    onUpload={async (files) => {
                      for (const file of files) await props.onAttach?.(file);
                      setAttachNonce((count) => count + 1);
                    }}
                  />
                )}

                {/* ux-pass-2 · the one history (`screens/history/`), as on every other record —
                 *Aktivitás* was a list of sentences over its own unpaged wire shape. */}
                <CollapsibleHistory type="person" id={person.id} />
              </div>
            </div>

            {/* ── the rail ─────────────────────────────────────────────────────────────────── */}
            <div
              className={`flex min-w-0 flex-col gap-3 ${wide ? 'order-1 lg:order-2 lg:w-[308px] lg:shrink-0 lg:overflow-y-auto lg:py-5 lg:pr-5 lg:pl-3' : 'order-2'}`}
            >
              {/**
               * ⚠️ **`Állapot` is its own box with the consequence written under it**, which is the
               * mockup's shape and the right one: deactivating somebody is not a field edit, and the
               * sentence *"nem lép be és nem rögzít időt"* is what makes it safe to offer inline.
               */}
              <IntegrationBox
                t={t}
                person={person}
                identities={props.identities}
                canManage={props.canPairIdentity && person.deletedAt === null}
                onPair={props.onPairIdentity}
              />

              <section className="bg-card rounded-xl border p-3 shadow-sm">
                <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                  {t('people.column.status')}
                </div>
                <Select
                  inline
                  className="mt-1.5 w-full"
                  value={person.status === 'invited' ? 'invited' : person.status}
                  disabled={readOnly || person.status === 'invited'}
                  aria-label={t('people.column.status')}
                  onChange={(event) => {
                    props.onStatus(event.target.value === 'active' ? 'active' : 'deactivated');
                  }}
                  options={
                    person.status === 'invited'
                      ? [{ value: 'invited', label: statusLabel(t, 'invited') }]
                      : [
                          { value: 'active', label: statusLabel(t, 'active') },
                          { value: 'deactivated', label: statusLabel(t, 'deactivated') },
                        ]
                  }
                />
                <p className="text-muted-foreground mt-2 text-[11.5px] leading-relaxed">
                  {/* 30 Oct 2026 · an invitee was told they were deactivated. */}
                  {person.status === 'active'
                    ? t('people.status.activeNote')
                    : person.status === 'invited'
                      ? t('people.status.invitedNote')
                      : t('people.status.deactivatedNote')}
                </p>
              </section>

              {/**
               * ⚠️⚠️ **REBUILT ON `people.markup.html` 333–460, WHICH IS A LIST OF ALIGNED ROWS AND
               * NOT A FORM.** This was ten stacked `Field`s — each a label above a bordered control —
               * so a rail of read-mostly facts looked like something waiting to be filled in. The
               * mockup puts a 92px label column beside a chrome-less control at 30px, so the box
               * reads as facts and becomes editable under the pointer.
               *
               * ⚠️ **The ORDER is the mockup's**, not ours: identity first (mail, phone), then what
               * the person is to the workspace (role, time tracking, employment), then where they sit
               * (team, manager, location, timezone, language), then the dates.
               *
               * ⚠️ **Three of the mockup's rows are ABSENT and that is deliberate**, flagged rather
               * than invented: *Erőforrás* (resource planning), *Szabadság* (leave) and *Alap szerep*
               * (a default project role) have no schema, no endpoint and no decision behind them.
               */}
              <DetailBox title={t('people.facts')}>
                {/**
                 * ⚠️⚠️ **THE NAME IS TWO ROWS NOW — item 6.** The header still draws the whole name
                 * in the reader's own order (`personName`); this is where the two halves are edited,
                 * because the order a name is DRAWN in is the reader's and the halves themselves are
                 * the person's.
                 *
                 * ⚠️ **An empty commit is ignored rather than sent.** `updatePersonRequestSchema`
                 * puts `.min(1)` on both halves, so there is no "clear one half" on the wire — half
                 * a split name is not a state anything downstream can draw, and `display_name` is
                 * composed from the pair.
                 */}
                <DetailRow label={t('people.field.familyName')}>
                  <InlineText
                    value={person.familyName ?? ''}
                    disabled={readOnly}
                    onCommit={(value) => {
                      if (value !== '') props.onPatch({ familyName: value });
                    }}
                  />
                </DetailRow>

                <DetailRow label={t('people.field.givenName')}>
                  <InlineText
                    value={person.givenName ?? ''}
                    disabled={readOnly}
                    onCommit={(value) => {
                      if (value !== '') props.onPatch({ givenName: value });
                    }}
                  />
                </DetailRow>

                {/* ⚠️ Read-only: the address is the identity (`hard-won.md` C6-10), and changing it
                  is an account-level flow with a confirmation, not a rail edit. */}
                <DetailRow label={t('people.field.email')}>
                  <DetailValue mono>{person.email}</DetailValue>
                </DetailRow>

                <DetailRow label={t('people.field.phone')}>
                  <InlineText
                    value={person.phone ?? ''}
                    disabled={readOnly}
                    onCommit={(value) => {
                      props.onPatch({ phone: value === '' ? null : value });
                    }}
                  />
                </DetailRow>

                {/**
                 * ⚠️⚠️ **ONE ROLE PER PERSON, PICKED HERE — 13 Oct 2026, the product owner.** This
                 * row was read-only, arguing that roles merge and a dropdown would say something
                 * false; the ruling is that a person has exactly one, which is the mockup's own
                 * permission-level picker. With nowhere else to change it, a role could only be
                 * chosen on the invitation. `decisions.md` R-56.
                 *
                 * ⚠️ *Nincs jogosultsági szint* is offered only while it is TRUE — somebody invited
                 * before roles existed — never as a choice to make.
                 */}
                <DetailRow label={t('people.column.roles')}>
                  {props.onRole === undefined ||
                  readOnly ||
                  props.roleOptions === undefined ||
                  props.roleOptions.length === 0 ? (
                    <DetailValue>{person.roles[0]?.name ?? t('people.kpi.noRole')}</DetailValue>
                  ) : (
                    <InlineSelect
                      value={person.roles[0]?.id ?? ''}
                      disabled={false}
                      onChange={(next) => {
                        if (next !== '') props.onRole?.(next);
                      }}
                      options={[
                        ...(person.roles.length === 0
                          ? [{ value: '', label: t('people.kpi.noRole') }]
                          : []),
                        ...props.roleOptions.map((role) => ({ value: role.id, label: role.name })),
                      ]}
                    />
                  )}
                </DetailRow>

                <DetailRow label={t('people.field.tracks')}>
                  <InlineSelect
                    value={person.tracksTime ? 'true' : 'false'}
                    disabled={readOnly}
                    onChange={(next) => {
                      props.onPatch({ tracksTime: next === 'true' });
                    }}
                    options={[
                      { value: 'true', label: t('people.tracks.yes') },
                      { value: 'false', label: t('people.tracks.no') },
                    ]}
                  />
                </DetailRow>

                {/**
                 * ⚠️⚠️ **ux-pass-2 · WHETHER THIS PERSON IS PLANNED AS A RESOURCE.** `0130`'s
                 * `resource_planned` decided who the resource views list and who an allocation can
                 * name, and nothing on any screen could change it. The phase 5 mockup's
                 * `persPlanItems` is the row; its two descriptions ride on the label's tooltip,
                 * because `Select` draws no second line under an option.
                 *
                 * ⚠️ U-20 · **locked while they do not log time**, and the tooltip says why:
                 * switching logging off on the row above has already switched this off.
                 */}
                <DetailRow
                  label={t('people.field.resource')}
                  title={
                    !person.tracksTime
                      ? t('people.plan.needsTracking')
                      : person.resourcePlanned
                        ? t('people.plan.yesHint')
                        : t('people.plan.noHint')
                  }
                >
                  <InlineSelect
                    value={person.resourcePlanned ? 'true' : 'false'}
                    disabled={readOnly || !person.tracksTime}
                    onChange={(next) => {
                      props.onPatch({ resourcePlanned: next === 'true' });
                    }}
                    options={[
                      { value: 'true', label: t('people.plan.yesLong') },
                      { value: 'false', label: t('people.plan.no') },
                    ]}
                  />
                </DetailRow>

                <DetailRow label={t('people.field.employment')}>
                  <InlineSelect
                    value={person.employmentType}
                    disabled={readOnly}
                    onChange={(next) => {
                      props.onPatch({ employmentType: next });
                    }}
                    options={[
                      { value: 'internal', label: employmentLabel(t, 'internal') },
                      { value: 'contractor', label: employmentLabel(t, 'contractor') },
                    ]}
                  />
                </DetailRow>

                {/**
                 * ⚠⚠ **C58 · SZENIORITÁS, AND IT IS NOT THE RATE CARD.** A rate card says what
                 * an hour costs and what it sells for, on a timeline (`0127`); this says what
                 * somebody IS. They correlate and they are not the same — two seniors on different
                 * cards, a medior on a rate nobody has revisited — and the finance dashboard's
                 * breakdown exists to show that spread. Deriving one from the other would make the
                 * panel answer its own question.
                 *
                 * ⚠ **The first option CLEARS it**, because *nobody has said* is a real state:
                 * `0135` adds the column with no default rather than asserting a level about every
                 * existing member on a migration.
                 */}
                <DetailRow label={t('people.field.seniority')}>
                  <InlineSelect
                    value={person.seniority ?? ''}
                    disabled={readOnly}
                    onChange={(next) => {
                      props.onPatch({ seniority: next === '' ? null : next });
                    }}
                    options={[
                      { value: '', label: t('person.seniority.none') },
                      ...SENIORITY_LEVELS.map((level) => ({
                        value: level,
                        label: seniorityLabel(t, level),
                      })),
                    ]}
                  />
                </DetailRow>

                {/* R-60 · any number of teams, from the list Settings → Teams keeps. The whole set
                  is sent on every change — the person's PATCH replaces it (`teamIds`). */}
                <DetailRow label={t('people.field.teams')}>
                  <MultiSelect
                    inline
                    /* U-14 · a list of records, so it searches. */
                    searchable
                    searchPlaceholder={t('list.searchOptions')}
                    label={t('people.field.teams')}
                    placeholder={t('people.field.noTeam')}
                    values={person.teams.map((team) => team.id)}
                    disabled={readOnly}
                    onValuesChange={(teamIds) => {
                      props.onPatch({ teamIds });
                    }}
                    options={props.teams.map((team) => ({ value: team.id, label: team.name }))}
                  />
                </DetailRow>

                {/**
                 * ⚠️⚠️ **WHETHER THIS PRODUCT KEEPS THIS PERSON'S LEAVE, AND IT BELONGS IN *RÉSZLETEK*.**
                 * The setting decides whether they appear in the quota grid and whether they may
                 * file a request at all — and the quota grid's own footnote names the people it is
                 * off for. It lived only in that footnote, which meant the decision could be READ on
                 * a settings screen and MADE nowhere: `PUT /workspace/leave/managed/:id` had no
                 * caller in the app.
                 *
                 * ⚠️ **Not the same control as the manager below it.** Leave approval follows the
                 * org chart; whether leave is managed here at all is a separate fact — somebody on a
                 * payroll system elsewhere still has a manager.
                 */}
                <DetailRow label={t('people.field.leave')}>
                  <InlineSelect
                    value={person.leaveManaged ? 'managed' : 'external'}
                    /* ⚠️ `people.manage`, which is what the server checks — not `settings.manage`.
                     Gating on the wrong one would offer a control that is always refused. */
                    disabled={readOnly || props.onLeaveManaged === undefined}
                    onChange={(next) => {
                      props.onLeaveManaged?.(next === 'managed');
                    }}
                    options={[
                      { value: 'managed', label: t('people.leave.managed') },
                      { value: 'external', label: t('people.leave.external') },
                    ]}
                  />
                </DetailRow>

                {/**
                 * 17 Oct 2026 · *Alap szerep* — the mockup's `persDroleItems`. What a new project
                 * membership starts from, and what the Utilizáció dashboard counts this person as.
                 * ⚠️ The first option CLEARS it: *nobody has said* is a real state.
                 */}
                <DetailRow label={t('people.field.defaultRole')}>
                  <InlineSelect
                    value={person.defaultProjectRole?.id ?? ''}
                    disabled={readOnly || props.projectRoles === undefined}
                    onChange={(next) => {
                      props.onPatch({ defaultProjectRoleId: next === '' ? null : next });
                    }}
                    options={[
                      { value: '', label: t('people.field.noDefaultRole') },
                      ...(
                        props.projectRoles ??
                        (person.defaultProjectRole === null ? [] : [person.defaultProjectRole])
                      ).map((role) => ({ value: role.id, label: role.name })),
                    ]}
                  />
                </DetailRow>

                <DetailRow label={t('people.field.manager')}>
                  <InlineSelect
                    /* U-14 · a list of records, so it searches. */
                    searchable
                    searchPlaceholder={t('list.searchOptions')}
                    value={person.managerMemberId ?? ''}
                    disabled={readOnly}
                    onChange={(next) => {
                      props.onPatch({ managerMemberId: next === '' ? null : next });
                    }}
                    options={[
                      { value: '', label: t('people.field.noManager') },
                      ...props.managers.map((manager) => ({
                        value: manager.id,
                        label: manager.name,
                      })),
                    ]}
                  />
                </DetailRow>

                <DetailRow label={t('people.field.location')}>
                  <InlineText
                    value={person.location ?? ''}
                    disabled={readOnly}
                    onCommit={(value) => {
                      props.onPatch({ location: value === '' ? null : value });
                    }}
                  />
                </DetailRow>

                {/**
                 * ⚠️⚠️ **A SEARCHABLE PICKER SINCE ITEM 49 — it was a free-text field.** A typo saved
                 * a zone the database refuses (`is_valid_timezone` asks Postgres to resolve the
                 * name), and the refusal arrived as a validation error about a string nobody could
                 * see was wrong. The list is the runtime's own IANA database, which is the same
                 * authority the column checks against — `timezones.ts` carries that argument.
                 *
                 * ⚠️ **`searchable`, because there are around four hundred of them.** `Select`'s own
                 * note puts the line at ten; this is the clearest case above it in the product.
                 */}
                <DetailRow label={t('people.field.timezone')}>
                  <InlineSelect
                    value={person.timezone}
                    disabled={readOnly}
                    searchable
                    searchPlaceholder={t('people.field.timezoneSearch')}
                    options={zoneOptions(person.timezone)}
                    /* ⚠️ Not nullable — a member always has one, and `''` would fail the schema's
                     * `min(1)` rather than clearing anything. */
                    onChange={(value) => {
                      if (value !== '') props.onPatch({ timezone: value });
                    }}
                  />
                </DetailRow>

                {/*
                ⚠️⚠️ **THE LANGUAGE ROW IS GONE — item 50 and D8.** A member row carried a
                `language` that decided nothing: every screen reads the LOCALE from the account's
                own preferences (`preferences-section.tsx`), which is where somebody chooses the
                language of the product they are looking at. A second field, set by an
                administrator on somebody else's behalf, could only ever disagree with it — and
                did, silently, because nothing read it.

                ⚠️ The column and the wire field stay for now: dropping them is a migration and a
                contract change, and `debt.md` carries the row. What is removed is the control that
                invited somebody to set a value with no effect.
              */}

                {/* ⚠️ `startsOn` is read-only in the mockup too — it is when the person joined, and
                  the row states a fact rather than offering to rewrite history. */}
                <DetailRow label={t('people.field.startsOn')}>
                  <DetailValue mono>{person.startsOn ?? '–'}</DetailValue>
                </DetailRow>

                <DetailRow label={t('people.field.endsOn')}>
                  {/* ⚠️ **The picker, not `InlineText type="date"`** — `date-field.tsx` carries the
                    argument. `InlineText` keeps a draft and commits on blur, which a calendar does
                    not need: a click IS the commit. */}
                  <DateField
                    label={t('people.field.endsOn')}
                    value={person.endsOn}
                    disabled={readOnly}
                    buttonClassName="h-7 border-transparent shadow-none"
                    onChange={(endsOn) => {
                      props.onPatch({ endsOn: endsOn === '' ? null : endsOn });
                    }}
                  />
                </DetailRow>
              </DetailBox>

              {/**
               * ┌─────────────────────────────────────────────────────────────────────┐
               * │ ⚠️⚠️ MUNKAREND — ITS OWN CARD, WHICH IS WHERE THE MOCKUP PUTS IT.    │
               * └─────────────────────────────────────────────────────────────────────┘
               *
               * It was a row in the middle of *Részletek*, between a start date and an end date,
               * where a seven-day pattern cannot be drawn and the reader has to take *"Full-time
               * (40h)"* on trust. The mockup gives it a card: the name and the frame on one line,
               * then the seven days as chips, with *Módosítás* in the title strip.
               *
               * ⚠️ **Read-only here, and the action LEAVES for the settings screen.** A schedule is
               * a timeline (C43): changing it means adding an assignment FROM a date, which is a
               * two-field decision. A control here would either hide the date — silently rewriting
               * history — or grow into that screen with fewer affordances.
               */}
              <DetailBox
                title={t('people.field.schedule')}
                actions={
                  props.onOpenSchedules === undefined ? undefined : (
                    <button
                      type="button"
                      className="text-primary text-[12px] font-medium"
                      onClick={props.onOpenSchedules}
                    >
                      {t('people.schedule.change')}
                    </button>
                  )
                }
              >
                {props.schedule === null ? (
                  <p className="text-muted-foreground px-1.5 py-1 text-[12.5px]">
                    {t('people.field.noSchedule')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-2 px-1.5 py-1">
                    <p className="text-[12.5px]">
                      {`${props.schedule.name ?? t('settings.schedules.custom')} · ${
                        props.schedule.type === 'flex'
                          ? t('settings.schedules.type.flex')
                          : t('settings.schedules.type.fix')
                      } · ${t('people.field.scheduleFrom', {
                        hours: String(Number(props.schedule.weeklyHours)),
                        dur: fmt(props.schedule.weeklyHours),
                        from: props.schedule.fromDate,
                      })}`}
                    </p>
                    {/* ⚠️ A `flex` schedule has no daily hours to draw — P3-7 — so the chips say so
                      rather than printing seven zeroes that would read as *"works no days"*. */}
                    {props.schedule.type === 'flex' ? (
                      <p className="text-muted-foreground text-[11.5px]">
                        {t('settings.schedules.flexNote')}
                      </p>
                    ) : (
                      /**
                       * ⚠️⚠️ **A SEVEN-COLUMN GRID, NOT A WRAPPING ROW.** `flex-wrap` with a 42px
                       * minimum needs 300px to hold the week; the person sidebar is narrower than
                       * that, so Sunday fell onto a line of its own and the card read as an
                       * eight-day week. A grid of seven equal fractions always holds seven, and the
                       * chips get narrower instead of leaving.
                       */
                      <div className="grid grid-cols-7 gap-1">
                        {props.schedule.days.map((hours, index) => (
                          <span
                            key={dayNames(locale)[index] ?? String(index)}
                            className={`flex min-w-0 flex-col items-center gap-0.5 rounded-md border px-0.5 py-1 ${
                              Number(hours) === 0 ? 'text-muted-foreground/60' : ''
                            }`}
                          >
                            <span className="text-[10px]">{dayNames(locale)[index]}</span>
                            <span className="text-[11px] tabular-nums">
                              {unit === 'days' ? fmtNum(hours) : `${String(Number(hours))}h`}
                            </span>
                            {/* 4 Nov 2026 · the chip is too narrow for `nap` beside the figure, so
                                the man-day's short marker sits beneath it (DAY_MARKER). */}
                            {unit === 'days' ? (
                              <span className="text-muted-foreground text-[9px] leading-none">
                                {DAY_MARKER}
                              </span>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </DetailBox>

              {/**
               * ┌────────────────────────────────────────────────────────────────┐
               * │ ⚠️⚠️ DÍJ — C51, AND IT SITS DIRECTLY UNDER MUNKAREND.               │
               * └────────────────────────────────────────────────────────────────┘
               *
               * Measured off the rendered mockup rather than guessed: the name line carries the
               * *Sablon* / *Egyéni* badge on the right, then three label/value rows — the unit with
               * the cost, the target, and the validity. **The margin is NOT drawn**, though the
               * mockup computes a `persRateMargin`: it is one of the keys nothing binds.
               *
               * ⚠️ **Read-only, and *Módosítás* LEAVES**, for the reason the schedule above does: a
               * rate is a timeline, and changing one means an assignment FROM a date.
               */}
              {!props.canSeeRates ? null : (
                <DetailBox
                  title={t('people.field.rate')}
                  actions={
                    props.onOpenRates === undefined ? undefined : (
                      <button
                        type="button"
                        className="text-primary text-[12px] font-medium"
                        onClick={props.onOpenRates}
                      >
                        {t('people.rate.change')}
                      </button>
                    )
                  }
                >
                  {props.rate === null ? (
                    <p className="text-muted-foreground px-1.5 py-1 text-[12.5px]">
                      {t('people.field.noRate')}
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2 px-2 py-1">
                        <span className="min-w-0 truncate text-[12.5px] font-medium">
                          {props.rate.templateName ?? t('settings.rates.customRate')}
                        </span>
                        <Badge variant={props.rate.source === 'template' ? 'secondary' : 'outline'}>
                          {props.rate.source === 'template'
                            ? t('settings.rates.fromTemplate')
                            : t('settings.rates.fromCustom')}
                        </Badge>
                      </div>
                      {/* ⚠️ A template whose first period has not started resolves to null figures —
                        the schema says so. The card explains that rather than printing dashes,
                        which read as *the card is broken*. */}
                      {props.rate.cost === null || props.rate.currencyCode === null ? (
                        <p className="text-muted-foreground px-2 py-1 text-[12px]">
                          {t('people.rate.pending')}
                        </p>
                      ) : (
                        <>
                          <DetailRow
                            label={
                              props.rate.unit === 'day'
                                ? t('people.rate.costDay')
                                : t('people.rate.costHour')
                            }
                          >
                            <DetailValue mono>
                              {formatMoney(
                                Number(props.rate.cost),
                                moneyOf(props.rate.currencyCode),
                              )}
                            </DetailValue>
                          </DetailRow>
                          <DetailRow label={t('people.rate.target')}>
                            <DetailValue mono>
                              {props.rate.target === null
                                ? '–'
                                : formatMoney(
                                    Number(props.rate.target),
                                    moneyOf(props.rate.currencyCode),
                                  )}
                            </DetailValue>
                          </DetailRow>
                        </>
                      )}
                      <DetailRow label={t('people.rate.validity')}>
                        <DetailValue mono>
                          {t('people.rate.from', { from: props.rate.fromDate })}
                        </DetailValue>
                      </DetailRow>
                    </>
                  )}
                </DetailBox>
              )}

              {/**
               * ┌─────────────────────────────────────────────────────────────────────┐
               * │ ⚠️ `0116` · THE WORKSPACE'S FOLDER *ABOUT* THIS PERSON.             │
               * └─────────────────────────────────────────────────────────────────────┘
               *
               * A contract, an NDA, a certificate. Asked for on 14 Sep, and a person has never had
               * anywhere to put one.
               *
               * ⚠️⚠️ **NOT `user_avatar`, AND THE DIFFERENCE IS WHO MAY LOOK.** The avatar is the
               * member's own — no workspace, governed by nobody else, shown wherever they appear.
               * This is the workspace's record about them: `people.view` to see that a document
               * exists, `people.manage` to put one there. `0116` carries the argument.
               */}

              {/* C10's person metafields. Absent when the workspace defines none. */}
              {/* ⚠️ The mockup's *Metafieldek* box is the same bordered strip over the same 92px
                rows — `people.markup.html` 462. */}
              {props.metafields.length > 0 && (
                <DetailBox title={t('people.metafields')}>
                  <MetafieldForm
                    /* ⚠️ **`inline` — item 51's second pass.** *"I want metafields to look exactly
                     like details above."* Same `DetailRow`, same 92px label column, same
                     chrome-less control, so the two boxes in this rail are one design. */
                    inline
                    fields={props.metafields}
                    values={person.metafields}
                    errors={{}}
                    /* ⚠️ **One column — item 51.** This box is the 308px rail, and the form's
                     `sm:grid-cols-2` measures the WINDOW: two 140px columns inside it, with the
                     box's width unused around them. `MetafieldForm`'s own note carries it. */
                    columns={1}
                    /* ⚠️ For the `user` type — the form knows nothing about members, so the caller
                     * supplies them. The same list the manager picker uses. */
                    members={props.managers.map((manager) => ({
                      id: manager.id,
                      name: manager.name,
                    }))}
                    disabled={readOnly}
                    onChange={(key, value) => {
                      props.onPatch({ metafields: { [key]: value } });
                    }}
                  />
                </DetailBox>
              )}
            </div>
          </div>
        </div>
      </div>
    </ScopedDropSurface>
  );
}

/**
 * A text field that commits on blur or Enter and abandons on Escape.
 *
 * ⚠️ **It holds its own draft string**, which `state-rules.md` §8 requires and which the tag editor
 * that made a comma untypeable is the standing example of. Deriving the value from the model makes
 * intermediate states unrepresentable.
 */
function InlineText({
  id,
  value,
  disabled,
  type,
  onCommit,
}: {
  /**
   * ⚠️ **Forwarded from `DetailRow`, which is what names this control.** A wrapper that swallows
   * the id leaves the row's `<label htmlFor>` pointing at nothing — `getByLabelText` says so, and
   * a screen reader announces "edit text" for every row in the box.
   */
  readonly id?: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly type?: string;
  readonly onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <TextInput
      {...(id === undefined ? {} : { id })}
      value={draft ?? value}
      disabled={disabled}
      /* ⚠️ The chrome goes and the focus ring stays — see `INLINE_INPUT`. */
      className={INLINE_INPUT}
      {...(type === undefined ? {} : { type })}
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onBlur={() => {
        const next = draft;
        setDraft(null);
        /* ⚠️ Unchanged is not an edit: a write would bump the version and audit a change nobody
         * made — the same guard the worklog editors carry. */
        if (next !== null && next !== value) onCommit(next);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          /* Handled — the modal around it stays open (`useModalSurface`). */
          event.preventDefault();
          setDraft(null);
        }
      }}
    />
  );
}

/**
 * A select that reads as text until it is used — the mockup's dropdown rows.
 *
 * ⚠️ **A native `<select>` wearing no chrome**, not a `DropdownMenu`. The mockup uses its own menu
 * component with a search box on two of these rows; ours is a plain select, which keeps the
 * keyboard behaviour the platform already gives and costs no popup inside a scrolling rail. Where
 * the mockup's search matters — a long manager or timezone list — that is a real difference, and
 * it is recorded here rather than pretended away.
 */
function InlineSelect({
  id,
  value,
  disabled,
  options,
  onChange,
  searchable = false,
  searchPlaceholder,
}: {
  /** Forwarded from `DetailRow` — see `InlineText`. */
  readonly id?: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly options: readonly { value: string; label: string }[];
  readonly onChange: (value: string) => void;
  /** ⚠️ Opt-in, per `Select`'s note: a four-item picker with a search box reads as broken. */
  readonly searchable?: boolean;
  readonly searchPlaceholder?: string;
}) {
  return (
    <Select
      {...(id === undefined ? {} : { id })}
      value={value}
      disabled={disabled}
      options={[...options]}
      searchable={searchable}
      {...(searchPlaceholder === undefined ? {} : { searchPlaceholder })}
      inline
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

/**
 * A titled section in the body column — `people.markup.html` 76–79.
 *
 * ⚠️ **The title sits OUTSIDE the bordered body**, unlike `DetailBox` where it is a strip inside
 * it. That is the mockup's own distinction: the rail's boxes are cards with a header, the body's
 * sections are a label over a table.
 */
function SectionBox({
  title,
  bare,
  actions,
  children,
}: {
  readonly title: string;
  /** No border around it — the history brings its own table. */
  readonly bare?: boolean;
  /** ⚠️ Items 52–54 · a search box or a picker, on the title row. */
  readonly actions?: React.ReactNode;
  readonly children: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex min-h-[26px] items-center gap-2">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {title}
        </div>
        <div className="flex-1" />
        {actions}
      </div>
      <div
        className={
          bare === true ? 'mt-1.5 flex flex-col' : 'mt-1.5 overflow-hidden rounded-lg border'
        }
      >
        {children}
      </div>
    </section>
  );
}

/** A chip that copies what it shows — the mockup's contact strip. */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Who this is — one row, used by all three surfaces. Item 48.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MODAL AND THE DRAWER SHOWED THE NAME AND NOTHING ELSE, AND THAT IS WHY THIS IS A
 * COMPONENT.** The full page's header gained the avatar, the status and the copyable chips; the
 * other two frames drew a bare `{name}` in their own headers, so the same person looked like two
 * different records depending on how you had opened them. Reported: *"in the modal and sidebar
 * view I want it in the header row as well — there you only see the name."*
 *
 * ⚠️ **One component rather than three copies**, because the previous arrangement is exactly
 * how the page came to have TWO headers in the first place: a strip written once for the body and
 * a title written again for the bar, neither knowing about the other.
 *
 * ⚠️ **It lays out as a row and sets no width.** Each frame supplies its own container - the
 * shell's header bar, the modal's 48px strip, the sheet's title slot - and a component that
 * assumed one of them would be wrong in the other two.
 */
export function PersonIdentity({
  t,
  person,
  name,
}: {
  readonly t: Translate;
  readonly person: Person;
  /** ⚠️ Already composed in the reader's order - `person-name.ts` decides that, not this. */
  readonly name: string;
}) {
  return (
    <>
      <span className="bg-muted grid size-6 shrink-0 place-items-center overflow-hidden rounded-full text-[10px] font-semibold">
        <AvatarFace seed={person.avatarSeed ?? undefined} fallback={initialsOf(name)} size={24} />
      </span>
      <span className="truncate text-[13.5px] font-medium">{name}</span>

      <Badge variant={person.status === 'active' ? 'default' : 'secondary'}>
        {person.deletedAt === null ? statusLabel(t, person.status) : t('people.status.deleted')}
      </Badge>

      <CopyChip icon="mail" label={person.email} title={t('people.copy.email')} />
      {person.phone === null ? null : (
        <CopyChip icon="smartphone" label={person.phone} title={t('people.copy.phone')} />
      )}
    </>
  );
}

function CopyChip({
  icon,
  label,
  title,
}: {
  readonly icon: IconName;
  readonly label: string;
  readonly title: string;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Tooltip asChild content={title}>
      <button
        type="button"
        aria-label={`${title}: ${label}`}
        className="text-muted-foreground hover:bg-accent hover:text-foreground flex h-6 max-w-full min-w-0 items-center gap-1.5 rounded-md px-1.5 text-[12px] transition-colors"
        onClick={() => {
          /**
           * ⚠️ **The clipboard can be refused** — a permissions prompt, an insecure origin, a
           * browser that has none. A rejected promise must not become an unhandled rejection, and
           * the tick simply does not appear.
           *
           * ⚠️ `navigator.clipboard` is typed as always present, so the guard is on the CALL rather
           * than an optional chain the linter can prove is dead.
           */
          void navigator.clipboard
            .writeText(label)
            .then(() => {
              setCopied(true);
              setTimeout(() => {
                setCopied(false);
              }, 1200);
            })
            .catch(() => undefined);
        }}
      >
        <Icon name={copied ? 'check' : icon} size={12} className="shrink-0" />
        <span className="min-w-0 truncate">{label}</span>
      </button>
    </Tooltip>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE PRODUCT'S OWN LIST, THREE TIMES — 14 Oct 2026, ux-pass-2.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Time logs, Projects, Tasks currently have search and filter on the bottom, which is a unique
 * separate view for this only. We need the same table view here that is globally present
 * everywhere else."* The three sections drew `MiniList` — five rows, a search box, ONE dropdown
 * and a hand-made pager, all under the rows. It is gone: each section is a `DataList` with the
 * toolbar on top, multi-column header sort and the settled `[<] [selector] [>] [total]` pager.
 *
 * ⚠️⚠️ **31 Oct 2026 · THE TIME LOGS AND THE TASKS PAGE ON THE SERVER; THE PROJECTS DO NOT.** A
 * person's hours run past a thousand rows and keep growing (UAT, 31 Oct), so those two sections ask
 * for one filtered, sorted page at a time (`useServerList`), and their menus come from the server's
 * facets rather than from the rows on one page. The projects are the few they have work on, already
 * in hand from the workspace's list — those are still searched and sorted in memory.
 *
 * ⚠️ **Ten rows a page, not the list default of fifty.** These are sections of a detail page
 * stacked one under the other; fifty worklogs would push the projects and the tasks a screen
 * away, which is the scroll item 52 removed in the first place.
 *
 * ⚠️ **No checkboxes** (`selectable={false}`): there is no bulk action over somebody's worklogs,
 * projects or tasks from their page, and a checkbox that feeds nothing is a control that lies.
 */
const SECTION_PAGE = 10;

/** ⚠️ What a date-range chip says. One `t()` per branch with a literal key — rule 7's shape. */
function rangeChip(t: Translate, value: string): string {
  const [from = '', to = ''] = value.split('..');
  if (from !== '' && to !== '') return t('list.rangeBoth', { from, to });
  if (from !== '') return t('list.rangeFromOnly', { from });
  return t('list.rangeToOnly', { to });
}

/** Distinct `{ value, label }` pairs, by value, in label order — every option list below. */
function distinctOptions<T>(
  rows: readonly T[],
  read: (row: T) => { value: string; label: string } | null,
): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    const option = read(row);
    if (option !== null && !seen.has(option.value)) seen.set(option.value, option.label);
  }
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The person's time logs.
 *
 * ⚠️ **Month and project are the two filters it already had** (item 52) — the month picker sat
 * on the section's heading and the project one under the rows; both are toolbar filters now. A
 * date range joins them, because *"the first two weeks of September"* is not a month.
 */
/** The *Sortétel* filter's value for hours no line item holds — and `?logs=unknown` in the address. */
const UNKNOWN_LINE_ITEM = 'unknown';

/**
 * The days a `yyyy-mm` month and a `from..to` range leave between them, as `from`/`to`. Wire dates
 * compare as text, so the later start and the earlier end are a string comparison.
 */
function dayWindow(month: string, range: string): { from: string; to: string } {
  const [rangeFrom = '', rangeTo = ''] = range.split('..');
  let from = rangeFrom;
  let to = rangeTo;
  if (month !== '') {
    const last = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
    const first = `${month}-01`;
    const end = `${month}-${String(last).padStart(2, '0')}`;
    from = from === '' || first > from ? first : from;
    to = to === '' || end < to ? end : to;
  }
  return { from, to };
}

function PersonLogList({
  personId,
  facets,
  nonce,
  loading,
}: {
  readonly personId: string;
  readonly facets: WorklogFacets | null;
  readonly nonce: number;
  readonly loading: boolean;
}) {
  const t = useT();
  const { fmt } = useDuration();
  /**
   * ⚠️⚠️ 28 Oct 2026 · **THE LOGOLÁS GRID'S RED MARK OPENS THIS LIST FILTERED**, by
   * `?logs=unknown`. Read once as the starting value; changing the filter writes it back (and
   * clearing it removes it), so a reload shows what is on screen rather than what the link said.
   */
  const [params, setParams] = useSearchParams();
  const [lineItem, setLineItemState] = useState(() =>
    params.get('logs') === UNKNOWN_LINE_ITEM ? UNKNOWN_LINE_ITEM : '',
  );
  const setLineItem = (value: string): void => {
    setLineItemState(value);
    if ((value === UNKNOWN_LINE_ITEM) !== (params.get('logs') === UNKNOWN_LINE_ITEM)) {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === UNKNOWN_LINE_ITEM) next.set('logs', UNKNOWN_LINE_ITEM);
          else next.delete('logs');
          return next;
        },
        { replace: true },
      );
    }
  };
  /* ⚠️ `ListToolbar` lives in `@forecastify/ui` and cannot reach `useT`, so a date bound takes
     the app's calendar vocabulary as a prop — `contracts-screen.tsx` does the same. */
  const calendar = useCalendarLabels();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);
  const [month, setMonth] = useState('');
  const [project, setProject] = useState('');
  const [dates, setDates] = useState('');

  /**
   * 3 Nov 2026 · ADAPTIVE · with projects chosen, the line item filter offers only the line items
   * their hours on THOSE projects went to — the same server facets, asked with the projects. The
   * project facet stays unnarrowed, or choosing one would hide the others. A chosen line item the
   * selection no longer carries is dropped once the answer arrives.
   */
  const session = useSession();
  const narrowedLineItems = useFilterOptions(
    async () => {
      if (project === '') return null;
      const response = await session.request(
        `/worklogs/facets?memberId=${encodeURIComponent(personId)}&projectId=${encodeURIComponent(project)}`,
      );
      if (!response.ok) return null;
      const parsed = worklogFacetsSchema.safeParse(await response.json().catch(() => null));
      return parsed.success ? parsed.data.lineItems : null;
    },
    `${personId}|${project}|${String(nonce)}`,
  );
  const lineItemOptions = project === '' ? (facets?.lineItems ?? []) : (narrowedLineItems ?? []);
  const narrowedIds = useMemo(
    () =>
      project === '' || narrowedLineItems === null
        ? null
        : new Set([UNKNOWN_LINE_ITEM, ...narrowedLineItems.map((one) => one.id)]),
    [project, narrowedLineItems],
  );
  useEffect(() => {
    /* The state setter, not `setLineItem`: the unknown item is never pruned, so the address it
       mirrors is never the one changing here. */
    if (narrowedIds !== null && lineItem !== '' && !narrowedIds.has(lineItem)) {
      setLineItemState('');
    }
  }, [narrowedIds, lineItem]);

  /**
   * ⚠️ One page, from the server, with every filter on the wire. The month and the date range are
   * both days, so they meet as one `from`/`to` — the later start and the earlier end. Two that do
   * not overlap ask for nothing and get nothing, which is what the two filters together mean.
   */
  const list = useServerList({
    path: '/worklogs',
    params: {
      memberId: personId,
      search: query.trim(),
      ...dayWindow(month, dates),
      projectId: project,
      ...(lineItem === UNKNOWN_LINE_ITEM ? { unknownLineItem: '1' } : { lineItemId: lineItem }),
      sort: formatSort(sort) ?? '',
    },
    schema: worklogSchema,
    limit: SECTION_PAGE,
    nonce,
  });

  /* ⚠️ Sorted on the server's columns (`WORKLOG_SORT`): hours on the number, not on `4:30`. */
  const rows: DataListRow[] = list.rows.map((entry) => ({
    id: entry.id,
    cells: {
      day: { text: entry.workedOn, mono: true, muted: true },
      task: { text: entry.taskKey, mono: true, title: `${entry.taskKey} · ${entry.taskTitle}` },
      /* 28 Oct 2026 · an unknown hour says so, in the grid's red, rather than *Belső projekt*. */
      project: entry.unknownLineItem
        ? { text: t('dash.row.unknownLineItem'), dot: 'var(--status-bad)' }
        : entry.projectName === null
          ? { text: t('projects.internal'), muted: true }
          : {
              text: entry.projectName,
              ...(entry.projectColor === null ? {} : { dot: entry.projectColor }),
            },
      /* ⚠️ An em-dash for an absent note, never a blank cell. */
      note: { text: entry.note ?? '–', muted: true },
      hours: { text: fmt(entry.hours), mono: true, bold: true },
    },
  }));

  const filtered =
    query.trim() !== '' || month !== '' || project !== '' || lineItem !== '' || dates !== '';

  return (
    <DataList
      columnsKey="person-logs"
      card={{ title: 'task', context: 'day', defaults: { details: ['hours', 'project', 'note'] } }}
      selectable={false}
      isLoading={loading || list.loading}
      columns={[
        { key: 'day', label: t('logs.column.day'), width: 110, sortKey: 'worked_on' },
        { key: 'task', label: t('time.column.task'), width: 110, sortKey: 'task' },
        { key: 'project', label: t('task.field.project'), width: 170, sortKey: 'project' },
        { key: 'note', label: t('time.column.note'), sortKey: 'note' },
        {
          key: 'hours',
          label: t('time.column.hours'),
          width: 90,
          align: 'right',
          sortKey: 'hours',
        },
      ]}
      rows={rows}
      page={list.page}
      sort={sort}
      onSortChange={setSort}
      search={query}
      onSearchChange={setQuery}
      filters={[
        {
          /* ⚠️ The months their hours fall in, newest first — from the server's facets, so the
             filter cannot offer a month with nothing in it. */
          key: 'month',
          label: t('people.logs.month'),
          value: month,
          onChange: setMonth,
          options: (facets?.months ?? []).map((value) => ({ value, label: value })),
        },
        {
          /* ⚠️ Item 52 · by PROJECT — *"how much of this went to Haystack?"* The projects their
             hours carry rather than the workspace's, so it never offers a value that empties
             the list. */
          key: 'project',
          label: t('task.field.project'),
          /* 3 Nov 2026 · several — a comma list the server ORs (`list-filter.ts`). */
          kind: 'multi' as const,
          value: project,
          onChange: setProject,
          options: (facets?.projects ?? []).map((one) => ({ value: one.id, label: one.name })),
        },
        {
          /* ⚠️ 28 Oct 2026 · *Ismeretlen sortételek* FIRST, then the line items their hours went
             to — from the facets, like the project filter, so it never offers an empty choice. */
          key: 'lineItem',
          label: t('people.logs.lineItem'),
          value: lineItem,
          onChange: setLineItem,
          searchable: true,
          options: [
            { value: UNKNOWN_LINE_ITEM, label: t('people.logs.unknownLineItems') },
            ...lineItemOptions.map((one) => ({ value: one.id, label: one.name })),
          ],
        },
        {
          key: 'date',
          label: t('time.column.date'),
          value: dates,
          onChange: setDates,
          kind: 'range' as const,
          input: 'date' as const,
          calendar,
          today: toWireDate(new Date()),
          options: [],
          display: (value: string) => rangeChip(t, value),
        },
      ]}
      empty={{
        icon: 'clock',
        title: filtered ? t('people.list.noMatch') : t('people.logs.emptyTitle'),
        body: filtered ? t('people.list.noMatchBody') : t('people.logs.empty'),
      }}
      labels={{
        ...listLabels(t),
        table: t('people.tab.logs'),
        search: t('people.logs.search'),
      }}
    />
  );
}

/**
 * The projects they are on.
 *
 * ⚠️ **Client is the filter it already had** (item 53 — a person on eleven projects is usually on
 * them for two or three clients). Status joins it: *"which of these are still running"* is the
 * other question this list is read with.
 */
function PersonProjectList({
  projects,
  loading,
}: {
  readonly projects: readonly Project[];
  readonly loading: boolean;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);
  const [client, setClient] = useState('');
  const [status, setStatus] = useState('');

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return projects.filter(
      (project) =>
        (needle === '' ||
          `${project.name} ${project.clientName ?? ''}`.toLowerCase().includes(needle)) &&
        /* ⚠️ `internal` cannot collide with a client id — those are uuids. */
        /* 3 Nov 2026 · each filter is several values — OR within, AND across. */
        (client === '' || filterItems(client).includes(project.clientId ?? INTERNAL)) &&
        (status === '' || filterItems(status).includes(project.status)),
    );
  }, [projects, query, client, status]);

  const rows: DataListRow[] = sortInMemory(shown, sort, {
    name: (one) => one.name,
    client: (one) => one.clientName ?? '',
    /* ⚠️ The lifecycle's own order, not the translated word's alphabet. */
    status: (one) => PROJECT_STATUSES.indexOf(one.status),
    start: (one) => one.startsOn ?? '',
    end: (one) => one.endsOn ?? '',
    tracked: (one) => Number(one.trackedHours),
  }).map((project) => ({
    id: project.id,
    cells: {
      name: { text: project.name, bold: true, dot: project.color },
      /* ⚠️ *Belső projekt* — a name, not an em-dash, as on the projects list. */
      client: {
        text: project.clientName ?? t('projects.internal'),
        muted: project.clientName === null,
      },
      status: { badge: { label: t(statusKey(project.status)), variant: 'secondary' as const } },
      start: { text: project.startsOn ?? '–', mono: true, muted: true },
      end: { text: project.endsOn ?? '–', mono: true, muted: true },
      tracked: { text: fmt(project.trackedHours), mono: true, bold: true },
    },
  }));

  const paged = useRowPages(rows, JSON.stringify([query, sort, client, status]), SECTION_PAGE);
  const filtered = query.trim() !== '' || client !== '' || status !== '';

  return (
    <DataList
      columnsKey="person-projects"
      card={{
        title: 'name',
        context: 'client',
        defaults: { badge: 'status', details: ['start', 'end', 'tracked'] },
      }}
      selectable={false}
      isLoading={loading}
      columns={[
        { key: 'name', label: t('projects.column.name'), sortKey: 'name' },
        { key: 'client', label: t('projects.column.client'), width: 170, sortKey: 'client' },
        { key: 'status', label: t('projects.column.status'), width: 130, sortKey: 'status' },
        { key: 'start', label: t('projects.column.start'), width: 110, sortKey: 'start' },
        { key: 'end', label: t('projects.column.end'), width: 110, sortKey: 'end' },
        {
          key: 'tracked',
          label: t('project.fact.tracked'),
          width: 110,
          align: 'right',
          sortKey: 'tracked',
        },
      ]}
      rows={paged.rows}
      page={paged.page}
      sort={sort}
      onSortChange={setSort}
      search={query}
      onSearchChange={setQuery}
      filters={[
        {
          key: 'client',
          label: t('projects.column.client'),
          kind: 'multi' as const,
          value: client,
          onChange: setClient,
          options: distinctOptions(projects, (project) =>
            project.clientId === null || project.clientName === null
              ? { value: INTERNAL, label: t('projects.internal') }
              : { value: project.clientId, label: project.clientName },
          ),
        },
        {
          key: 'status',
          label: t('projects.column.status'),
          kind: 'multi' as const,
          value: status,
          onChange: setStatus,
          /* ⚠️ Only the statuses their projects are in, in the lifecycle's order. */
          options: PROJECT_STATUSES.filter((value) =>
            projects.some((project) => project.status === value),
          ).map((value) => ({ value, label: t(statusKey(value)) })),
        },
      ]}
      empty={{
        icon: 'folder',
        title: filtered ? t('people.list.noMatch') : t('people.projects.emptyTitle'),
        body: filtered ? t('people.list.noMatchBody') : t('people.projects.empty'),
      }}
      labels={{
        ...listLabels(t),
        table: t('people.tab.projects'),
        search: t('people.projects.search'),
      }}
    />
  );
}

/** The client filter's value for a project with no client. */
const INTERNAL = 'internal';

/**
 * Their tasks.
 *
 * ⚠️⚠️ **THE TASK LIST'S OWN COLUMNS AND CELLS** — `task-columns.ts`, which exists so no list
 * draws a task its own way. Minus the assignee, who is the person this page is about.
 *
 * ⚠️ **Status is the filter it already had** (item 54 — *"what is still open on them"*). Project
 * and priority join it. All three come from the server's facets over their tasks, so a workspace
 * that renamed its statuses gets its own words and no menu offers a value that empties the list.
 */
function PersonTaskList({
  personId,
  facets,
  nonce,
  loading,
  onOpen,
}: {
  readonly personId: string;
  readonly facets: TaskFacets | null;
  readonly nonce: number;
  readonly loading: boolean;
  readonly onOpen: (task: Task) => void;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);
  const [status, setStatus] = useState('');
  const [project, setProject] = useState('');
  const [priority, setPriority] = useState('');

  /**
   * ⚠️ One page, from the server — `task-columns.ts`'s `sortKey`s are the server's `TASK_SORT`
   * names, so status sorts by the workspace's own sequence rather than the category's.
   */
  const list = useServerList({
    path: '/tasks',
    params: {
      assigneeMemberId: personId,
      search: query.trim(),
      statusId: status,
      projectId: project,
      priorityId: priority,
      sort: formatSort(sort) ?? '',
    },
    schema: taskSchema,
    limit: SECTION_PAGE,
    nonce,
  });
  const rows = taskRows(t, list.rows, NO_AVATARS, [], fmt);

  const filtered = query.trim() !== '' || status !== '' || project !== '' || priority !== '';

  return (
    <DataList
      columnsKey="person-tasks"
      card={{
        title: 'title',
        context: 'key',
        defaults: {
          badge: 'status',
          avatar: 'assignee',
          details: ['priority', 'due', 'estimate', 'project'],
        },
      }}
      selectable={false}
      isLoading={loading || list.loading}
      columns={taskColumns(t, PERSON_TASK_COLUMNS)}
      rows={rows}
      page={list.page}
      sort={sort}
      onSortChange={setSort}
      search={query}
      onSearchChange={setQuery}
      filters={[
        {
          key: 'status',
          label: t('project.tasks.column.status'),
          /* 3 Nov 2026 · comma lists the server ORs (`list-filter.ts`). */
          kind: 'multi' as const,
          value: status,
          onChange: setStatus,
          options: (facets?.statuses ?? []).map((one) => ({ value: one.id, label: one.name })),
        },
        {
          key: 'project',
          label: t('task.field.project'),
          kind: 'multi' as const,
          value: project,
          onChange: setProject,
          options: (facets?.projects ?? []).map((one) => ({ value: one.id, label: one.name })),
        },
        {
          key: 'priority',
          label: t('project.tasks.column.priority'),
          kind: 'multi' as const,
          value: priority,
          onChange: setPriority,
          options: (facets?.priorities ?? []).map((one) => ({ value: one.id, label: one.name })),
        },
      ]}
      onRowClick={(id) => {
        const task = list.rows.find((candidate) => candidate.id === id);
        if (task !== undefined) onOpen(task);
      }}
      empty={{
        icon: 'list-checks',
        title: filtered ? t('people.list.noMatch') : t('people.tasks.emptyTitle'),
        body: filtered ? t('people.list.noMatchBody') : t('people.tasks.empty'),
      }}
      labels={{
        ...listLabels(t),
        table: t('people.tab.tasks'),
        search: t('people.tasks.search'),
      }}
    />
  );
}

/** The task list's columns, minus the assignee — every row here is assigned to this person. */
const PERSON_TASK_COLUMNS: readonly TaskColumnKey[] = [
  'key',
  'title',
  'project',
  'status',
  'priority',
  'due',
  'estimate',
  'tracked',
];

/** ⚠️ No assignee column, so no faces to fetch. */
const NO_AVATARS: ReadonlyMap<string, string> = new Map();

/**
 * The month grid over the person's hours — `people.markup.html` 96–110.
 *
 * ⚠️⚠️ **THE MONTH IS THEIR NEWEST WITH HOURS, NOT TODAY'S.** Picking "this month" would leave the
 * grid empty for anybody whose last entry was in the previous one, which reads as a broken chart
 * rather than as a quiet month. Since 31 Oct 2026 the route reads that month alone — the newest of
 * the facets' months — rather than every hour the person has, and hands it here.
 *
 * ⚠️ **A MONDAY-FIRST grid**, like every other calendar in this product. `getDay()` is
 * Sunday-first, so the lead is shifted; getting that wrong moves every cell one column and nothing
 * complains about it.
 */
function MonthOfHours({
  month: shownMonth,
  worklogs,
}: {
  readonly month: string | null;
  readonly worklogs: readonly Worklog[];
}) {
  const t = useT();
  /* The day cells are too narrow for `nap`; a man-day cell carries the short marker instead. */
  const { fmt, fmtNum, unit } = useDuration();

  const anchor = shownMonth === null ? toWireDate(new Date()) : `${shownMonth}-01`;
  const year = Number(anchor.slice(0, 4));
  const month = Number(anchor.slice(5, 7));

  const first = new Date(year, month - 1, 1);
  const days = new Date(year, month, 0).getDate();
  const lead = (first.getDay() + 6) % 7;

  const byDay = new Map<string, number>();
  for (const entry of worklogs) {
    byDay.set(entry.workedOn, (byDay.get(entry.workedOn) ?? 0) + Number(entry.hours));
  }

  const shown = [...byDay.entries()].filter(([day]) => day.startsWith(anchor.slice(0, 7)));
  const total = shown.reduce((sum, [, hours]) => sum + hours, 0);
  const busiest = shown.reduce((max, [, hours]) => Math.max(max, hours), 0);

  return (
    <div className="border-b">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <span className="text-[12px] tabular-nums">
          {anchor.slice(0, 7)} · {fmt(total)}
        </span>
      </div>

      <div className="grid grid-cols-7 gap-1 p-3">
        {WEEKDAY_KEYS.map((key) => (
          <div key={key} className="text-muted-foreground pb-1 text-center text-[11px] font-medium">
            {t(key)}
          </div>
        ))}

        {Array.from({ length: lead }, (_, index) => (
          <div key={`lead-${String(index)}`} />
        ))}

        {Array.from({ length: days }, (_, index) => {
          const iso = `${anchor.slice(0, 8)}${String(index + 1).padStart(2, '0')}`;
          const hours = byDay.get(iso) ?? 0;
          return (
            <Tooltip key={iso} asChild content={`${iso} · ${fmt(hours)}`}>
              <div
                className="flex h-[38px] flex-col justify-between rounded-md border px-1.5 py-1"
                style={
                  hours === 0
                    ? undefined
                    : {
                        background: tintFor(hours, busiest),
                      }
                }
              >
                <span className="text-muted-foreground flex items-baseline justify-between text-[10.5px] leading-none">
                  {index + 1}
                  {unit === 'days' && hours !== 0 ? (
                    <span className="text-[8.5px]">{DAY_MARKER}</span>
                  ) : null}
                </span>
                <span className="text-right text-[10.5px] leading-none tabular-nums">
                  {hours === 0 ? '' : fmtNum(hours)}
                </span>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

/**
 * ⚠️ Tinted by the day's share of the BUSIEST day, so the shape of a month is readable. Against
 * the month's total, every cell would be nearly white.
 */
function tintFor(hours: number, busiest: number): string {
  const share = Math.min(42, 10 + Math.round((hours / Math.max(busiest, 1)) * 32));
  return `color-mix(in oklch, var(--primary) ${String(share)}%, transparent)`;
}

/** Monday-first, and the keys are literals so `i18n:check` can see them. */
const WEEKDAY_KEYS = [
  'time.dow.mon',
  'time.dow.tue',
  'time.dow.wed',
  'time.dow.thu',
  'time.dow.fri',
  'time.dow.sat',
  'time.dow.sun',
] as const;

/**
 * The symbol `formatMoney` needs, from the STATIC catalogue rather than the workspace's list.
 *
 * ⚠️ **The person screen does not read `/workspace/currencies` and should not start.** It draws
 * one figure in one currency; fetching the workspace's whole currency settings to learn that HUF
 * is written `Ft` would be a tenth request on a screen that already makes nine. The catalogue is
 * the same twelve rows the settings screen offers when adding one, so the symbol agrees — and a
 * code outside it falls back to the code itself, which is what `rate-card-section.tsx` does too.
 */
function moneyOf(code: string): { code: string; symbol: string } {
  return CURRENCY_CATALOGUE.find((one) => one.code === code) ?? { code, symbol: code };
}

/**
 * Monday first, like `wireDayOfWeek` and like the stored array.
 *
 * ⚠️ `Intl`, not seven catalogue keys — `schedules-section.tsx` sets the rule and the reason: a
 * hand-written weekday table is a second thing to get wrong in two languages.
 */
function dayNames(locale: string): readonly string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  /* 2024-01-01 was a Monday. UTC, so a negative offset cannot walk it back to Sunday. */
  return [0, 1, 2, 3, 4, 5, 6].map((offset) =>
    format.format(new Date(Date.UTC(2024, 0, 1 + offset))),
  );
}

/**
 * ⚠ **One literal key per level.** A computed `t(`person.seniority.${level}`)` is invisible to the
 * catalogue check, which is how a key goes missing in one language and nothing notices.
 */
export function seniorityLabel(
  t: ReturnType<typeof useT>,
  level: (typeof SENIORITY_LEVELS)[number],
): string {
  if (level === 'junior') return t('person.seniority.junior');
  if (level === 'medior') return t('person.seniority.medior');
  if (level === 'senior') return t('person.seniority.senior');
  if (level === 'lead') return t('person.seniority.lead');
  return t('person.seniority.principal');
}

/**
 * ⚠️ **The mockup's own four colours, and they are NOT the identity palette.** A billing bucket is
 * a category with a fixed meaning — teal is fixed-price everywhere in the product — where an
 * identity hue is assigned per person and means nothing on its own.
 */
const BILLING_COLOR: Record<BillingKind, string> = {
  fixed: 'var(--status-ok)',
  time_and_material: 'var(--status-info)',
  non_billable: 'var(--status-idle)',
  none: 'var(--status-warn)',
};

/** ⚠️ One `t()` per branch with a literal key — a computed one is invisible to `i18n:check`. */
function billingLabel(t: Translate, kind: BillingKind): string {
  if (kind === 'fixed') return t('people.split.fixed');
  if (kind === 'time_and_material') return t('people.split.tm');
  if (kind === 'non_billable') return t('people.split.nonBillable');
  return t('people.split.none');
}
